// src/components/blog/components/BlogMeta.tsx

'use client'

import Link from 'next/link'
import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import type { BlogPost } from '~/lib/blog/types'
import { formatDate, formatDateTime } from '~/lib/utils/date'

/**
 * Props for the BlogMeta component
 * @interface BlogMetaProps
 * @property {BlogPost} post - The blog post to display metadata for
 * @property {boolean} [showTopic=true] - Whether to show the post topic
 * @property {boolean} [showDate=true] - Whether to show the post date
 * @property {boolean} [showReadingTime=true] - Whether to show estimated reading time
 * @property {string} [className] - Optional CSS class name for styling
 */
interface BlogMetaProps {
  /** The blog post to display metadata for */
  post: BlogPost
  /** Whether to show the post topic */
  showTopic?: boolean
  /** Whether to show the post date */
  showDate?: boolean
  /** Whether to show estimated reading time */
  showReadingTime?: boolean
  /** Optional CSS class name for styling */
  className?: string
}

/**
 * Displays metadata about a blog post including date, reading time, and topic
 * 
 * @component BlogMeta
 * @example
 * ```tsx
 * <BlogMeta 
 *   post={post}
 *   showTopic={true}
 *   showDate={true}
 *   showReadingTime={true}
 * />
 * ```
 */
export function BlogMeta({ 
  post,
  showTopic = true,
  showDate = true,
  showReadingTime = true,
  className 
}: BlogMetaProps): JSX.Element {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn("flex flex-wrap items-center gap-x-4 text-sm text-[var(--glass-text-color)] dark:text-[var(--glass-text-dark-color)]", className)}
    >
      {/* Date */}
      {showDate && (
        <time
          dateTime={formatDateTime(post.datetime)}
          className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          {formatDate(post.datetime, 'full')}
        </time>
      )}

      {/* Topic */}
      {showTopic && post.topic && (
        <>
          <span className="h-1 w-1 rounded-full bg-indigo-400/50 dark:bg-indigo-500/50" />
          <Link
            href={post.topic.href}
            className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            {post.topic.title}
          </Link>
        </>
      )}

      {/* Reading Time */}
      {showReadingTime && post.readingTime && (
        <>
          <span className="h-1 w-1 rounded-full bg-indigo-400/50 dark:bg-indigo-500/50" />
          {post.readingTime} min read
        </>
      )}
    </motion.div>
  )
} 